import { Controller } from "@hotwired/stimulus"
import { scrollContainerFor } from "../utils/scroll_container"

export default class extends Controller {
  static targets = ["button"]
  // A reader is asked for in screenfuls ("once they are a screen deep"), which is a
  // different number on a phone than on a desktop; everywhere else names its own pixel depth.
  static values = { threshold: { type: Number, default: 400 }, viewports: Number }

  connect() {
    this.scrollHandler = this.throttle(() => {
      this.updateVisibility()
    }, 16)

    // Capture phase: in the admin area the page scroll belongs to an element, not the document.
    window.addEventListener('scroll', this.scrollHandler, true)

    // A page can open already scrolled — an in-page anchor, or a restored position.
    this.updateVisibility()
  }

  disconnect() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, true)
    }
  }

  scrollToTop() {
    scrollContainerFor(this.element).scrollTo({
      top: 0,
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth'
    })
  }

  updateVisibility() {
    if (!this.hasButtonTarget) return

    const scroller = scrollContainerFor(this.element)

    if (scroller.scrollTop > this.showThresholdFor(scroller)) {
      this.buttonTarget.classList.add('visible')
    } else {
      this.buttonTarget.classList.remove('visible')
    }
  }

  showThresholdFor(scroller) {
    if (this.viewportsValue > 0) return this.viewportsValue * scroller.clientHeight

    return this.thresholdValue
  }

  get prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  }

  // Throttled with a trailing call, not just a leading one: a reader who flicks the page
  // and stops mid-window would otherwise have their last scroll event dropped, leaving the
  // button describing a position they have already left.
  throttle(callback, limit) {
    let timer = null
    let missed = false

    const invoke = () => {
      callback()
      timer = setTimeout(() => {
        timer = null
        if (!missed) return

        missed = false
        invoke()
      }, limit)
    }

    return () => {
      if (timer) {
        missed = true
        return
      }

      invoke()
    }
  }
}
